/**
 * Per-tenant feature flags — the single declaration.
 *
 * Each flag gates one ops-dashboard surface. A missing key means "use the
 * registry default"; only an explicit `false` hides a surface that defaults on.
 *
 * ⚠️ These gate NAV ONLY. The `/api/*` routes check permissions
 * (auth/permissions.ts), never these flags — so a flag is a "what did this
 * tenant buy / what do they want to see" switch, not an access boundary. Do not
 * reach for a flag when you mean a permission.
 *
 * This file exists because the same list used to live in four hand-maintained
 * places (the worker's `ClientFeatures`, `ops/src/auth/AuthContext.tsx`,
 * `ops/src/screens/admin/shared.tsx`, and the label array inside
 * `ParkifyAdmin.tsx`) and had already drifted: the AuthContext copy was missing
 * `stripe`, so `hasFeature('stripe')` wasn't even typable and ClientAdmin read
 * the field off the other mirror instead. One registry turns that class of drift
 * into a tsc failure.
 *
 * To add a flag: add one row below. The type, the defaults, the Parkify Admin
 * checkbox list, and the nav gate all derive from it.
 */

/**
 * One flag's declaration. `label` is what Parkify Admin → Clients renders.
 *
 * `key` is typed `string` rather than `FeatureFlagKey` on purpose: the key union
 * is DERIVED from the registry below, so narrowing it here would be circular.
 * The `as const satisfies` on FEATURE_FLAGS still pins each literal.
 */
export interface FeatureFlagSpec {
  key: string
  label: string
  /**
   * What an absent key means. `false` here is for surfaces we haven't built out
   * for client self-serve yet — they stay Parkify-configured until a client asks.
   */
  defaultOn: boolean
  /**
   * `section` — a top-level sidebar item; `false` removes it from the tenant's
   * nav and bounces any deep-link to it.
   * `subfeature` — something narrower inside a screen.
   */
  kind: 'section' | 'subfeature'
}

/**
 * The registry. Order is the order Parkify Admin renders the checkboxes.
 *
 * Four sidebar items are deliberately absent and NOT toggleable:
 *   • home           — the fallback a bounce lands on; can't be hidden
 *   • integrations   — role-gated to owner/admin (Parkify staff)
 *   • parkify_admin  — role-gated to owner/admin (Parkify staff)
 *   • client_admin   — role-gated to org leads; hiding it would strand a client
 *                      manager with no way to manage their team or Stripe
 *
 * `tenants` has no flag of its own either — it rides `validations` via
 * SCREEN_FEATURE_FLAG in ops/src/App.tsx.
 */
export const FEATURE_FLAGS = [
  { key: 'support',      label: 'Support',                              defaultOn: true,  kind: 'section' },
  { key: 'transactions', label: 'Transactions',                         defaultOn: true,  kind: 'section' },
  { key: 'disputes',     label: 'Disputes',                             defaultOn: true,  kind: 'section' },
  { key: 'zones',        label: 'Zones',                                defaultOn: true,  kind: 'section' },
  { key: 'qr',           label: 'QR codes',                             defaultOn: true,  kind: 'section' },
  // Permits + Validations aren't built out for client self-serve, so they
  // default OFF until a client is ready (flipped 2026-08-04).
  { key: 'permits',      label: 'Permits',                              defaultOn: false, kind: 'section' },
  { key: 'validations',  label: 'Validations (+ Tenants)',              defaultOn: false, kind: 'section' },
  { key: 'enforcement',  label: 'Enforcement',                          defaultOn: true,  kind: 'section' },
  { key: 'accounting',   label: 'Accounting',                           defaultOn: true,  kind: 'section' },
  { key: 'analytics',    label: 'Analytics',                            defaultOn: true,  kind: 'section' },
  { key: 'customers',    label: 'Customers',                            defaultOn: true,  kind: 'section' },
  // Hides the Subscribers tab inside Customers; the Customers screen itself
  // stays unless `customers` is also false.
  { key: 'subscribers',  label: 'Subscribers (Customers tab)',          defaultOn: true,  kind: 'subfeature' },
  // The Avery widget on every screen — off for a client running their own
  // assistant, or one not ready for the AI surface.
  { key: 'avery',        label: 'Avery (AI assistant)',                 defaultOn: true,  kind: 'subfeature' },
  // The Stripe tab inside Client Admin (self-service Connect onboarding +
  // payout entities). `false` hides it from the client's own roles — Parkify
  // staff still see it via "Viewing as", since turning it off means Parkify now
  // runs that client's Stripe onboarding for them.
  { key: 'stripe',       label: 'Stripe tab (self-service onboarding)',  defaultOn: true,  kind: 'subfeature' },
  // Reserved (numbered) bays — the whole surface: Locations → zone Overview → Bays, the
  // permit Reserved bays panel, program bay pools, the Patrol bay lookup and
  // the tenant portal's pool line. OFF by default while the feature is
  // unfinished (RESERVED-BAYS-SPEC phases 4–5 pending); the Worker refuses the
  // bay routes for a client without it, so this one IS an access gate, not nav.
  { key: 'reservedBays', label: 'Reserved bays (numbered spaces)',       defaultOn: false, kind: 'subfeature' },
] as const satisfies readonly FeatureFlagSpec[]

/** Every flag key. Derived — never hand-maintain a parallel union. */
export type FeatureFlagKey = typeof FEATURE_FLAGS[number]['key']

/**
 * The stored shape on a Client record. Every key optional: an absent key means
 * "registry default", which is what keeps adding a flag from needing a backfill.
 */
export type ClientFeatures = { [K in FeatureFlagKey]?: boolean }

/** Keys whose absence means OFF. Derived from the registry. */
export const DEFAULT_OFF_FEATURES: ReadonlySet<FeatureFlagKey> = new Set(
  FEATURE_FLAGS.filter(f => !f.defaultOn).map(f => f.key),
)

/**
 * Is `key` on for this tenant?
 *
 * The one place the "absent means default, explicit false means off" rule is
 * written down. Callers should not re-implement it — that is how the two ops
 * copies drifted apart in the first place.
 */
export function isFeatureEnabled(
  features: ClientFeatures | null | undefined,
  key: FeatureFlagKey,
): boolean {
  const flag = features?.[key]
  if (flag !== undefined) return flag
  return !DEFAULT_OFF_FEATURES.has(key)
}

/**
 * Per-USER flags. Same shape as the tenant map, different meaning: this narrows
 * one person's console inside a tenant that already has the surface.
 *
 * ⚠️ SUBTRACT-ONLY. A user flag can hide something the tenant has; it can never
 * reveal something the tenant doesn't (see isFeatureEnabledFor). That asymmetry
 * is the whole design: without it, a per-user toggle becomes a second, parallel
 * permission system able to grant a surface the tenant was never given — and
 * these flags are NOT an access boundary (routes check permissions, not flags),
 * so it would be a permission system that doesn't actually permit anything.
 *
 * If you find yourself wanting a user flag to GRANT access, you want a role
 * permission (auth/permissions.ts), not this.
 */
export type UserFeatures = ClientFeatures

/**
 * Is `key` on for this user, in this tenant?
 *
 * effective = tenant-enabled AND user-not-opted-out
 *
 * The single home for the layering rule, for the same reason isFeatureEnabled is
 * the single home for absent-means-default: two call sites re-implementing it is
 * exactly how the flag list fragmented into four copies in the first place.
 *
 * A user flag is read as an opt-OUT only: `false` hides, and anything else
 * (`true`, absent, no map at all) defers to the tenant. So `{permits: true}` on
 * a user whose tenant has Permits off still resolves to off.
 */
export function isFeatureEnabledFor(
  clientFeatures: ClientFeatures | null | undefined,
  userFeatures: UserFeatures | null | undefined,
  key: FeatureFlagKey,
): boolean {
  if (!isFeatureEnabled(clientFeatures, key)) return false
  return userFeatures?.[key] !== false
}

/**
 * An explicit map of every flag at its registry default.
 *
 * The onboarding wizard stamps this onto a new client so a tenant's surface is a
 * STORED FACT rather than a value recomputed from whatever the registry defaults
 * happen to be whenever the console next renders. That distinction is not
 * academic: flipping `permits`/`validations` to default-off on 2026-08-04
 * silently removed nav from every existing tenant, because not one of them had a
 * stored opinion to protect it.
 *
 * The tradeoff, stated plainly: a client created with an explicit map does NOT
 * pick up a later change to a flag's default. That is the intent — a surface
 * someone agreed to on an onboarding call shouldn't move because we later
 * changed our minds about defaults. Flags added to the registry AFTER a client
 * was created are still absent from its map, so those do follow the new default.
 */
export function defaultFeatures(): ClientFeatures {
  return Object.fromEntries(FEATURE_FLAGS.map(f => [f.key, f.defaultOn])) as ClientFeatures
}
